import { getCarQueryParams } from '../api/auth.js'
import {
	CAR_CHECKIN_ZONE_TOP_OFFSET_PCT,
	CAR_SEAL_ZONE_DEBUG_BG,
	CAR_PASSWORD_VERIFY_POINTS,
	CAR_SHAKE_PASSWORD_VERIFY_CODE,
	CAR_SHAKE_UI_EXTRA_TOP_OFFSET_PCT,
	CAR_SHAKE_VERIFY_RULE_CODE,
	CAR_SHAKE_WIN_MESSAGE,
	CAR_TOAST_SEAL_SUCCESS_MS
} from '../api/config.js'
import { showToast } from '../api/public.js'
import {
	applyStyleToElement,
	getCarvueAdjustedZoneAnchor,
	getCarvueFullWidthSquareZoneStyle,
	getCarvueSlideHeight
} from '../utils/act004-hotspot-style.js'
import { resolveImageUrl } from '../utils/image-url.js'
import { cvQuery } from '../utils/dom.js'
import { isAct004LinkCheckinZoneHotspot } from '../../../utils/act004-link-checkin-zone.js'
import {
	drawSignStoreLottery,
	getCarActivityUserId,
	getCarAuthStoreCode,
	getRulePointMeta,
	listSignStoreLotteryDrawRecords,
	verifySignStoreLotterySeal
} from '../api/signstore-api.js'

const STANDARD_W = 800
const STANDARD_H = 600

import {
	SHAKE_DELTA_THRESHOLD,
	SHAKE_DRAW_COOLDOWN_MS,
	SHAKE_DRAWING_DELAY_MS,
	SHAKE_MIN_DURATION_MS,
	SHAKE_PEAK_MIN_GAP_MS,
	SHAKE_PEAK_WINDOW_MS,
	SHAKE_PEAKS_REQUIRED,
	SHAKE_SAMPLE_INTERVAL_MS,
	SHAKE_VISUAL_DECAY,
	SHAKE_VISUAL_IMPULSE_ROT,
	SHAKE_VISUAL_IMPULSE_XY,
	SHAKE_VISUAL_MAX_DEG,
	SHAKE_VISUAL_MAX_PX,
	SHAKE_VISUAL_MIN_DELTA
} from './shake-motion-config.js'

const state = {
	hotspot: null,
	completed: false,
	verifyStatus: null,
	drawRecordId: null,
	wonImage: '',
	verifiedImage: '',
	drawing: false,
	verifyChecking: false,
	_gestureSubmitted: false,
	_lastDrawAt: 0,
	_lastTapDrawAt: 0,
	_motionHandler: null,
	_motionLast: { x: null, y: null, z: null, t: 0 },
	_motionVisualLast: { x: null, y: null, z: null },
	_shakePeakTimes: [],
	_shakeSessionStart: 0,
	_permissionGranted: false,
	_permissionRequesting: false,
	_listenerActive: false,
	_verifyResizeHandler: null,
	_shakeVisualRaf: 0,
	_shakeVisual: { x: 0, y: 0, rot: 0 },
	_lastShakeSizeTier: null
}

function normalizeLotteryConfigId(raw) {
	const n = parseInt(raw, 10)
	return Number.isFinite(n) && n > 0 ? n : null
}

function parsePageConfig(activity) {
	if (!activity) return {}
	let raw = activity.pageConfigJson != null ? activity.pageConfigJson : activity.pageConfig
	if (typeof raw === 'string') {
		try {
			raw = JSON.parse(raw)
		} catch (e) {
			raw = {}
		}
	}
	return raw && typeof raw === 'object' ? raw : {}
}

function getPageKeyFromQuery() {
	const q = getCarQueryParams()
	return (q.get('pageKey') || q.get('page_key') || '').trim()
}

function isShakeLotteryHotspot(h) {
	if (!h || String(h.type) !== '21') return false
	if (String(h.shakeAfterAction || 'lottery').toLowerCase() !== 'lottery') return false
	return !!normalizeLotteryConfigId(h.lotteryConfigId)
}

function findShakeHotspotInPage(page) {
	const list = page && Array.isArray(page.hotspots) ? page.hotspots : []
	return list.find(isShakeLotteryHotspot) || null
}

/** 从活动 pageConfigJson 解析摇一摇热点（type=21），车展 H5 独立页需跨页查找 */
export function findCarShakeHotspot(activity) {
	const pageConfig = parsePageConfig(activity)
	const pages = Array.isArray(pageConfig.pages) ? pageConfig.pages : []
	const pageKey = getPageKeyFromQuery()

	if (pageKey && pages.length) {
		const byKey = pages.find(function (p) {
			return String((p && p.pageKey) || '').trim() === pageKey
		})
		const idx = parseInt(pageKey, 10)
		const byIdx = Number.isFinite(idx) && pages[idx] ? pages[idx] : null
		const fromQuery = findShakeHotspotInPage(byKey || byIdx)
		if (fromQuery) return fromQuery
	}

	var i
	for (i = 0; i < pages.length; i++) {
		if (String((pages[i] && pages[i].pageName) || '').trim() === '摇享好礼') {
			const hit = findShakeHotspotInPage(pages[i])
			if (hit) return hit
		}
	}
	for (i = 0; i < pages.length; i++) {
		const hit = findShakeHotspotInPage(pages[i])
		if (hit) return hit
	}
	const root = Array.isArray(pageConfig.hotspots) ? pageConfig.hotspots : []
	return root.find(isShakeLotteryHotspot) || null
}

/** 与 findCarShakeHotspot 相同页序，返回摇一摇所在页（用于取联动打卡热区 type=22） */
function findCarShakePage(activity) {
	const pageConfig = parsePageConfig(activity)
	const pages = Array.isArray(pageConfig.pages) ? pageConfig.pages : []
	const pageKey = getPageKeyFromQuery()

	if (pageKey && pages.length) {
		const byKey = pages.find(function (p) {
			return String((p && p.pageKey) || '').trim() === pageKey
		})
		const idx = parseInt(pageKey, 10)
		const byIdx = Number.isFinite(idx) && pages[idx] ? pages[idx] : null
		if (findShakeHotspotInPage(byKey || byIdx)) return byKey || byIdx
	}

	var i
	for (i = 0; i < pages.length; i++) {
		if (String((pages[i] && pages[i].pageName) || '').trim() === '摇享好礼') {
			if (findShakeHotspotInPage(pages[i])) return pages[i]
		}
	}
	for (i = 0; i < pages.length; i++) {
		if (findShakeHotspotInPage(pages[i])) return pages[i]
	}
	return null
}

/** 优先使用同页联动打卡热区（type=22）的 left/top/尺寸，与 activity-multi 一致 */
function findLinkCheckinZoneAnchor(activity) {
	const page = findCarShakePage(activity)
	if (page && Array.isArray(page.hotspots)) {
		for (let i = 0; i < page.hotspots.length; i++) {
			if (isAct004LinkCheckinZoneHotspot(page.hotspots[i])) {
				return page.hotspots[i]
			}
		}
	}
	const pages = Array.isArray(parsePageConfig(activity).pages) ? parsePageConfig(activity).pages : []
	for (let p = 0; p < pages.length; p++) {
		const list = pages[p] && Array.isArray(pages[p].hotspots) ? pages[p].hotspots : []
		for (let i = 0; i < list.length; i++) {
			if (isAct004LinkCheckinZoneHotspot(list[i])) return list[i]
		}
	}
	return null
}

function normalizeHotspot(raw) {
	if (!raw) return null
	return {
		type: String(raw.type),
		index: parseInt(raw.index, 10) || 1,
		left: raw.left,
		top: raw.top,
		sizeWidth: raw.sizeWidth != null ? raw.sizeWidth : raw.size_width,
		sizeHeight: raw.sizeHeight != null ? raw.sizeHeight : raw.size_height,
		shakeBeforeImage: raw.shakeBeforeImage != null ? String(raw.shakeBeforeImage).trim() : '',
		shakeAfterAction: String(raw.shakeAfterAction || 'lottery').trim().toLowerCase(),
		lotteryConfigId: normalizeLotteryConfigId(raw.lotteryConfigId),
		shakeVerifyMode: String(raw.shakeVerifyMode || raw.shake_verify_mode || '').trim().toLowerCase(),
		shakeVerifyRuleCode: CAR_SHAKE_VERIFY_RULE_CODE
	}
}

function getHotspotKey(hotspot) {
	if (!hotspot) return ''
	const idx = parseInt(hotspot.index, 10)
	if (Number.isFinite(idx) && idx > 0) return 'idx-' + idx
	return hotspot.lotteryConfigId ? 'cfg-' + hotspot.lotteryConfigId : ''
}

function getSignstoreUserId() {
	try {
		const raw = window.localStorage.getItem('signstore_h5_user_info')
		const u = raw ? JSON.parse(raw) : null
		if (u && u.uid != null) return parseInt(u.uid, 10)
	} catch (e) {
		// ignore
	}
	return null
}

function resolvePrizeShowImage(prize) {
	if (!prize || typeof prize !== 'object') return ''
	return String(
		prize.lotteryShowImage ||
			prize.lottery_show_image ||
			prize.productImage ||
			prize.product_image ||
			''
	).trim()
}

function resolveVerifiedImage(prize) {
	if (!prize || typeof prize !== 'object') return ''
	return String(prize.lotteryVerifiedImage || prize.lottery_verified_image || '').trim()
}

function isVerifyRequired(hotspot) {
	if (!hotspot) return false
	if (hotspot.shakeVerifyMode === 'checkin') return true
	return !!hotspot.shakeVerifyRuleCode
}

export function isPendingVerify() {
	if (!state.completed) return false
	if (state.verifyStatus === 1) return false
	if (state.verifyStatus === 0) return true
	return isVerifyRequired(state.hotspot)
}

/** 与 activity-multi 一致：0 待核销，1 已核销 */
function normalizeVerifyStatus(record, prize, hotspot) {
	const vsRaw =
		record.verifyStatus != null
			? record.verifyStatus
			: record.verify_status != null
				? record.verify_status
				: prize && prize.verifyStatus != null
					? prize.verifyStatus
					: prize && prize.verify_status != null
						? prize.verify_status
						: null
	let vs = parseInt(vsRaw, 10)
	if (!Number.isFinite(vs)) {
		vs = isVerifyRequired(hotspot) ? 0 : 1
	}
	return vs === 1 ? 1 : 0
}

function currentDisplayImage() {
	if (!state.hotspot) return ''
	// 已核销 → lotteryVerifiedImage（无则回退中奖图）
	if (state.completed && state.verifyStatus === 1) {
		const verified = state.verifiedImage || state.wonImage
		if (verified) return resolveImageUrl(verified)
	}
	// 已中奖待核销 / 无需核销 → lotteryShowImage
	if (state.completed && state.wonImage) {
		return resolveImageUrl(state.wonImage)
	}
	// 未抽奖
	return resolveImageUrl(state.hotspot.shakeBeforeImage)
}

function clampShakeVisual(value, max) {
	return Math.max(-max, Math.min(max, value))
}

function canApplyShakeVisual() {
	return !!(state.hotspot && !state.drawing && !state.completed && state._permissionGranted)
}

function applyShakeVisualTransform() {
	const img = cvQuery('#homeShakePrizeImg')
	if (!img) return
	const v = state._shakeVisual
	if (!v.x && !v.y && !v.rot) {
		img.style.transform = ''
		img.classList.remove('is-shake-active')
		return
	}
	img.classList.add('is-shake-active')
	img.style.transform =
		'translate(' + v.x.toFixed(2) + 'px,' + v.y.toFixed(2) + 'px) rotate(' + v.rot.toFixed(2) + 'deg)'
}

function tickShakeVisualDecay() {
	state._shakeVisualRaf = 0
	if (!canApplyShakeVisual()) {
		resetShakeVisual()
		return
	}
	const v = state._shakeVisual
	v.x *= SHAKE_VISUAL_DECAY
	v.y *= SHAKE_VISUAL_DECAY
	v.rot *= SHAKE_VISUAL_DECAY
	if (Math.abs(v.x) < 0.08 && Math.abs(v.y) < 0.08 && Math.abs(v.rot) < 0.08) {
		resetShakeVisual()
		return
	}
	applyShakeVisualTransform()
	state._shakeVisualRaf = requestAnimationFrame(tickShakeVisualDecay)
}

function updateShakeVisualFromDelta(dx, dy, dz) {
	if (!canApplyShakeVisual()) return
	const mag = Math.abs(dx) + Math.abs(dy) + Math.abs(dz)
	if (mag < SHAKE_VISUAL_MIN_DELTA) return
	const v = state._shakeVisual
	v.x = clampShakeVisual(v.x + dx * SHAKE_VISUAL_IMPULSE_XY, SHAKE_VISUAL_MAX_PX)
	v.y = clampShakeVisual(v.y + dy * SHAKE_VISUAL_IMPULSE_XY, SHAKE_VISUAL_MAX_PX)
	v.rot = clampShakeVisual(v.rot + (dx + dz) * SHAKE_VISUAL_IMPULSE_ROT, SHAKE_VISUAL_MAX_DEG)
	applyShakeVisualTransform()
	if (!state._shakeVisualRaf) {
		state._shakeVisualRaf = requestAnimationFrame(tickShakeVisualDecay)
	}
}

function resetShakeVisual() {
	if (state._shakeVisualRaf) {
		cancelAnimationFrame(state._shakeVisualRaf)
		state._shakeVisualRaf = 0
	}
	state._shakeVisual = { x: 0, y: 0, rot: 0 }
	const img = cvQuery('#homeShakePrizeImg')
	if (img) {
		img.style.transform = ''
		img.classList.remove('is-shake-active')
	}
}

function getShakeSizeTier() {
	if (!state.completed) return 'idle'
	if (state.verifyStatus === 1) return 'verified'
	return 'won'
}

function syncShakeStageSize() {
	const stage = cvQuery('#homeShakeStage')
	if (!stage) return 'idle'
	stage.classList.remove('is-won', 'is-verified', 'is-tap-draw')
	const tier = getShakeSizeTier()
	if (tier === 'verified') {
		stage.classList.add('is-verified')
	} else if (tier === 'won') {
		stage.classList.add('is-won')
	} else {
		stage.classList.add('is-tap-draw')
	}
	return tier
}

function updatePrizeImage() {
	const img = cvQuery('#homeShakePrizeImg')
	const stage = cvQuery('#homeShakeStage')
	if (!img || !stage) return Promise.resolve()

	const tier = syncShakeStageSize()
	const src = currentDisplayImage()
	const prevSrc = img.getAttribute('data-shake-src') || ''
	const unchanged =
		prevSrc === src &&
		state._lastShakeSizeTier === tier &&
		img.complete &&
		img.naturalWidth > 0

	if (unchanged) {
		img.style.visibility = 'visible'
		stage.classList.add('is-shake-ready')
		if (state.completed || state.drawing) resetShakeVisual()
		return Promise.resolve()
	}

	stage.classList.remove('is-shake-ready')
	img.style.visibility = 'hidden'

	if (!src) {
		img.removeAttribute('src')
		img.removeAttribute('data-shake-src')
		state._lastShakeSizeTier = tier
		return Promise.resolve()
	}

	return new Promise(function (resolve) {
		let settled = false
		const finish = function () {
			if (settled) return
			settled = true
			img.removeEventListener('load', finish)
			img.removeEventListener('error', finish)
			img.style.visibility = 'visible'
			stage.classList.add('is-shake-ready')
			state._lastShakeSizeTier = tier
			if (state.completed || state.drawing) resetShakeVisual()
			resolve()
		}

		img.setAttribute('data-shake-src', src)
		img.addEventListener('load', finish)
		img.addEventListener('error', finish)
		img.src = src
		if (img.complete && img.naturalWidth) finish()
	})
}

function resolveVerifyZoneAnchor(activity) {
	const linkZone = findLinkCheckinZoneAnchor(activity)
	if (linkZone) return linkZone
	return state.hotspot
}

function applyVerifyZoneLayout() {
	const zone = cvQuery('#homeShakeVerifyZone')
	if (!zone) return
	const activity = window.__carActivityDetail
	const anchor = resolveVerifyZoneAnchor(activity)
	if (!anchor) return
	const shakeTopOffset =
		CAR_CHECKIN_ZONE_TOP_OFFSET_PCT + CAR_SHAKE_UI_EXTRA_TOP_OFFSET_PCT
	const adjusted = getCarvueAdjustedZoneAnchor(anchor, 50, shakeTopOffset)
	const slideH = getCarvueSlideHeight('.carvue-root .swiper-slide.home_slide')
	const boxStyle = getCarvueFullWidthSquareZoneStyle(adjusted, slideH, {
		opacity: '1',
		background: CAR_SEAL_ZONE_DEBUG_BG,
		touchAction: 'none',
		pointerEvents: 'auto',
		zIndex: '25'
	})
	applyStyleToElement(zone, boxStyle)
}

function bindVerifyZoneResize() {
	if (state._verifyResizeHandler || typeof window === 'undefined') return
	state._verifyResizeHandler = function () {
		if (isPendingVerify()) applyVerifyZoneLayout()
	}
	window.addEventListener('resize', state._verifyResizeHandler, { passive: true })
}

function unbindVerifyZoneResize() {
	if (state._verifyResizeHandler && typeof window !== 'undefined') {
		window.removeEventListener('resize', state._verifyResizeHandler)
	}
	state._verifyResizeHandler = null
}

function updateVerifyLayer() {
	const layer = cvQuery('#homeShakeVerifyLayer')
	const zone = cvQuery('#homeShakeVerifyZone')
	if (!layer || !zone) return
	const show = isPendingVerify()
	layer.hidden = !show
	layer.classList.toggle('is-show', show)
	zone.style.pointerEvents = show ? 'auto' : 'none'
	if (show) {
		applyVerifyZoneLayout()
		bindVerifyZoneResize()
	}
	triggerPasswordHotspotSync()
	if (!show) {
		state._gestureSubmitted = false
		unbindVerifyZoneResize()
	}
}

function updateDrawingHint() {}

function syncShakeListener() {
	destroyShakeListener()
	if (!state.hotspot || state.drawing) return
	if (state.completed) return
	if (!state._permissionGranted) return
	const DM = window.DeviceMotionEvent
	if (!DM) return
	state._motionHandler = function (event) {
		const acc = event && (event.accelerationIncludingGravity || event.acceleration)
		if (!acc) return
		const x = acc.x != null ? acc.x : 0
		const y = acc.y != null ? acc.y : 0
		const z = acc.z != null ? acc.z : 0
		processMotion(x, y, z)
	}
	window.addEventListener('devicemotion', state._motionHandler, false)
	state._listenerActive = true
}

function destroyShakeListener() {
	if (state._motionHandler) {
		window.removeEventListener('devicemotion', state._motionHandler, false)
		state._motionHandler = null
	}
	state._listenerActive = false
	state._motionLast = { x: null, y: null, z: null, t: 0 }
	state._motionVisualLast = { x: null, y: null, z: null }
	state._shakePeakTimes = []
	state._shakeSessionStart = 0
	resetShakeVisual()
}

function registerShakePeak(now) {
	const last = state._shakePeakTimes[state._shakePeakTimes.length - 1]
	if (last != null && now - last < SHAKE_PEAK_MIN_GAP_MS) {
		return isShakeReady(now)
	}
	const cutoff = now - SHAKE_PEAK_WINDOW_MS
	state._shakePeakTimes = state._shakePeakTimes.filter(function (t) {
		return t >= cutoff
	})
	state._shakePeakTimes.push(now)
	if (state._shakePeakTimes.length === 1) {
		state._shakeSessionStart = now
	}
	return isShakeReady(now)
}

function isShakeReady(now) {
	if (state._shakePeakTimes.length < SHAKE_PEAKS_REQUIRED) return false
	if (!state._shakeSessionStart) return false
	return now - state._shakeSessionStart >= SHAKE_MIN_DURATION_MS
}

function processMotion(x, y, z) {
	if (state.drawing || !state.hotspot) return
	if (state.completed && !isPendingVerify()) return
	const now = Date.now()
	const visualLast = state._motionVisualLast
	if (visualLast.x != null && !state.completed && !state.drawing) {
		updateShakeVisualFromDelta(x - visualLast.x, y - visualLast.y, z - visualLast.z)
	}
	state._motionVisualLast = { x: x, y: y, z: z }
	if (now - state._motionLast.t < SHAKE_SAMPLE_INTERVAL_MS) return
	if (state._motionLast.x != null) {
		const dx = x - state._motionLast.x
		const dy = y - state._motionLast.y
		const dz = z - state._motionLast.z
		const delta = Math.abs(dx) + Math.abs(dy) + Math.abs(dz)
		if (
			delta >= SHAKE_DELTA_THRESHOLD &&
			registerShakePeak(now) &&
			now - state._lastDrawAt > SHAKE_DRAW_COOLDOWN_MS
		) {
			state._shakePeakTimes = []
			state._shakeSessionStart = 0
			triggerDraw()
		}
	}
	state._motionLast = { x: x, y: y, z: z, t: now }
}

/** 在用户点击/触摸时静默请求 iOS 运动传感器权限（无 toast） */
export function tryShakeMotionPermissionFromUserGesture() {
	requestMotionPermissionFromGesture()
}

function needsIosMotionPermission() {
	const DM = window.DeviceMotionEvent
	return !!(DM && typeof DM.requestPermission === 'function')
}

function onShakeMotionPermissionGranted() {
	state._permissionGranted = true
	syncShakeListener()
}

/** 须在用户手势回调中同步调用 requestPermission（iOS 要求） */
function requestMotionPermissionFromGesture() {
	if (state._permissionGranted || state._permissionRequesting) return
	const DM = window.DeviceMotionEvent
	if (!DM) return
	if (typeof DM.requestPermission !== 'function') {
		onShakeMotionPermissionGranted()
		return
	}
	state._permissionRequesting = true
	DM.requestPermission()
		.then(function (result) {
			if (result === 'granted') {
				onShakeMotionPermissionGranted()
			}
		})
		.catch(function () {
			// 用户拒绝时不额外弹 toast，避免打断体验
		})
		.finally(function () {
			state._permissionRequesting = false
		})
}

function extractErrorMessage(err, res) {
	if (res && res.msg) return String(res.msg)
	if (err && err.response && err.response.data && err.response.data.msg) {
		return String(err.response.data.msg)
	}
	if (err && err.message) return String(err.message)
	return '操作失败'
}

function applyDrawRecord(record) {
	if (!record) return
	const prize = record.prize && typeof record.prize === 'object' ? record.prize : null
	state.completed = true
	state.wonImage =
		resolvePrizeShowImage(record) ||
		(prize ? resolvePrizeShowImage(prize) : '') ||
		state.wonImage
	state.verifiedImage =
		resolveVerifiedImage(record) ||
		(prize ? resolveVerifiedImage(prize) : '') ||
		state.verifiedImage
	state.verifyStatus = normalizeVerifyStatus(record, prize, state.hotspot)
	const dr = record.drawRecordId != null ? record.drawRecordId : record.draw_record_id
	if (dr != null) state.drawRecordId = parseInt(dr, 10)
	if (state.hotspot) {
		state.hotspot.shakeVerifyRuleCode = CAR_SHAKE_VERIFY_RULE_CODE
	}
}

/** 进入页时查询 /signstore/lottery/h5/drawRecords，回显未摇/待核销/已核销图 */
async function loadDrawRecords() {
	const activityUserId = getCarActivityUserId()
	if (!activityUserId || !state.hotspot) {
		console.warn('[carvue] loadDrawRecords 跳过：activityUserId 或热点未就绪')
		return
	}
	try {
		const res = await listSignStoreLotteryDrawRecords({ activityUserId })
		if (!res || Number(res.status) !== 200 || !Array.isArray(res.data)) {
			console.warn('[carvue] loadDrawRecords 无有效数据', res && res.msg)
			return
		}
		const targetIdx = parseInt(state.hotspot.index, 10)
		const cfgId = state.hotspot.lotteryConfigId
		let item = res.data.find(function (row) {
			const idx = parseInt(row.hotspotIndex != null ? row.hotspotIndex : row.hotspot_index, 10)
			return Number.isFinite(targetIdx) && idx === targetIdx
		})
		if (!item && cfgId) {
			item = res.data.find(function (row) {
				const cid = parseInt(row.lotteryConfigId != null ? row.lotteryConfigId : row.lottery_config_id, 10)
				return cid === cfgId
			})
		}
		if (item) {
			applyDrawRecord(item)
		}
	} catch (e) {
		console.warn('loadDrawRecords', e)
	}
}

async function triggerDraw() {
	if (!state.hotspot || state.drawing || state.completed) return
	const lotteryConfigId = state.hotspot.lotteryConfigId
	const userId = getSignstoreUserId()
	const activityUserId = getCarActivityUserId()
	if (!lotteryConfigId) {
		showToast('error', '未配置抽奖规则')
		return
	}
	if (!userId) {
		showToast('error', '用户信息未就绪')
		return
	}
	state.drawing = true
	state._lastDrawAt = Date.now()
	resetShakeVisual()
	updateDrawingHint()
	destroyShakeListener()
	let res = null
	try {
		const payload = { userId, lotteryConfigId }
		if (activityUserId) payload.activityUserId = activityUserId
		/** 车展约定：摇享好礼固定 pointIndex=1，不用活动热点 index（避免写成 3 等） */
		const shakePointMeta = getRulePointMeta(CAR_SHAKE_VERIFY_RULE_CODE)
		if (shakePointMeta && shakePointMeta.pointIndex > 0) {
			payload.hotspotIndex = shakePointMeta.pointIndex
		}
		if (isVerifyRequired(state.hotspot)) payload.shakeVerifyRequired = true
		res = await drawSignStoreLottery(payload)
		if (!res || Number(res.status) !== 200 || !res.data) {
			const msg = extractErrorMessage(null, res)
			if (msg.indexOf('已参与') >= 0) await loadDrawRecords()
			showToast('error', msg)
			return
		}
		await delay(SHAKE_DRAWING_DELAY_MS)
		applyDrawRecord(res.data)
		await updatePrizeImage()
		updateVerifyLayer()
		if (isPendingVerify()) {
			showToast('success', CAR_SHAKE_WIN_MESSAGE)
		} else {
			const name = res.data.productName
				? '恭喜获得 ' + res.data.productName
				: CAR_SHAKE_WIN_MESSAGE
			showToast('success', name)
		}
	} catch (e) {
		showToast('error', extractErrorMessage(e, res))
	} finally {
		state.drawing = false
		updateDrawingHint()
		syncShakeListener()
	}
}

function delay(ms) {
	return new Promise(function (resolve) {
		setTimeout(resolve, ms)
	})
}

function collectTouchPoints(zone, event) {
	const rect = zone.getBoundingClientRect()
	const touches = event.touches && event.touches.length ? event.touches : []
	const points = []
	for (let i = 0; i < touches.length; i++) {
		const t = touches[i]
		const rx = t.clientX - rect.left
		const ry = t.clientY - rect.top
		const x = Math.round((rx / rect.width) * STANDARD_W)
		const y = Math.round((ry / rect.height) * STANDARD_H)
		points.push([x, y])
	}
	return points
}

function bindVerifyZone() {
	const zone = cvQuery('#homeShakeVerifyZone')
	if (!zone || zone.__carvueVerifyBound) return
	zone.__carvueVerifyBound = true
	const onTouch = function (event) {
		if (isTouchOnShakePasswordHotspot(event)) {
			if (event.cancelable) event.preventDefault()
			import('./password-hotspot.js').then(function (m) {
				if (m && m.openPasswordDialog && m.PWD_HOTSPOT_KIND) {
					m.openPasswordDialog(m.PWD_HOTSPOT_KIND.SHAKE)
				}
			})
			return
		}
		if (!isPendingVerify() || state.verifyChecking || state._gestureSubmitted) return
		event.preventDefault()
		const points = collectTouchPoints(zone, event)
		if (points.length !== 4) return
		state._gestureSubmitted = true
		doVerifySeal(points)
	}
	zone.addEventListener('touchstart', onTouch, { passive: false })
	zone.addEventListener('touchmove', onTouch, { passive: false })
	zone.addEventListener('touchend', function (e) {
		if (!e.touches || e.touches.length === 0) state._gestureSubmitted = false
	})
}

function triggerPasswordHotspotSync() {
	import('./password-hotspot.js')
		.then(function (m) {
			if (m && typeof m.syncAllPasswordHotspots === 'function') {
				m.syncAllPasswordHotspots()
			}
		})
		.catch(function () {})
}

function isTouchOnShakePasswordHotspot(event) {
	const btn = cvQuery('#homeShakePasswordHotspot')
	if (!btn) return false
	const touch = event.touches && event.touches[0]
		? event.touches[0]
		: event.changedTouches && event.changedTouches[0]
			? event.changedTouches[0]
			: null
	if (!touch) return event.target === btn || (btn.contains && btn.contains(event.target))
	const rect = btn.getBoundingClientRect()
	return (
		touch.clientX >= rect.left &&
		touch.clientX <= rect.right &&
		touch.clientY >= rect.top &&
		touch.clientY <= rect.bottom
	)
}

export async function submitCarShakePasswordVerify(password) {
	if (String(password || '').trim() !== CAR_SHAKE_PASSWORD_VERIFY_CODE) {
		showToast('error', '密码错误')
		return
	}
	if (!isPendingVerify()) {
		showToast('info', '当前无需核销')
		return
	}
	if (state.verifyChecking) return
	await doVerifySeal(CAR_PASSWORD_VERIFY_POINTS.slice())
}

async function doVerifySeal(touchPoints) {
	if (!state.hotspot || state.verifyChecking) return
	const ruleCode = CAR_SHAKE_VERIFY_RULE_CODE
	const userId = getSignstoreUserId()
	const activityUserId = getCarActivityUserId()
	const drawRecordId = state.drawRecordId
	if (!ruleCode) {
		showToast('error', '未配置核销规则')
		state._gestureSubmitted = false
		return
	}
	if (!drawRecordId || !userId) {
		showToast('error', '抽奖记录不存在')
		state._gestureSubmitted = false
		return
	}
	const authStoreCode = getCarAuthStoreCode()
	if (!authStoreCode) {
		showToast('error', '门店编码未配置')
		state._gestureSubmitted = false
		return
	}
	state.verifyChecking = true
	let res = null
	try {
		res = await verifySignStoreLotterySeal({
			drawRecordId,
			userId,
			ruleCode,
			touchPoints,
			activityUserId,
			authStoreCode
		})
		if (!res || Number(res.status) !== 200 || !res.data) {
			showToast('error', extractErrorMessage(null, res))
			return
		}
		state.verifyStatus = 1
		const vImg = resolveVerifiedImage(res.data)
		if (vImg) state.verifiedImage = vImg
		await updatePrizeImage()
		updateVerifyLayer()
		triggerPasswordHotspotSync()
		showToast('success', '核销成功', CAR_TOAST_SEAL_SUCCESS_MS)
		syncShakeListener()
	} catch (e) {
		showToast('error', extractErrorMessage(e, res))
	} finally {
		state.verifyChecking = false
		state._gestureSubmitted = false
	}
}

function canTapToDraw() {
	if (!state.hotspot || state.drawing || state.completed) return false
	const swiper = window.__carvueSwiper
	if (swiper && swiper.activeIndex !== 0) return false
	return true
}

function handleShakePrizeTapDraw(event) {
	if (!canTapToDraw()) return
	const now = Date.now()
	if (now - state._lastTapDrawAt < 400) return
	if (now - state._lastDrawAt <= SHAKE_DRAW_COOLDOWN_MS) return
	state._lastTapDrawAt = now
	if (event && event.cancelable) event.preventDefault()
	tryShakeMotionPermissionFromUserGesture()
	triggerDraw()
}

function bindShakePrizeTapToDraw() {
	const stage = cvQuery('#homeShakeStage')
	if (!stage || stage.__carvueTapDrawBound) return
	stage.__carvueTapDrawBound = true
	stage.__carvueTapDrawHandler = handleShakePrizeTapDraw
	stage.addEventListener('click', stage.__carvueTapDrawHandler)
	stage.addEventListener('touchend', stage.__carvueTapDrawHandler, { passive: false })
}

function unbindShakePrizeTapToDraw() {
	const stage = cvQuery('#homeShakeStage')
	if (!stage || !stage.__carvueTapDrawBound) return
	if (stage.__carvueTapDrawHandler) {
		stage.removeEventListener('click', stage.__carvueTapDrawHandler)
		stage.removeEventListener('touchend', stage.__carvueTapDrawHandler)
	}
	stage.__carvueTapDrawHandler = null
	stage.__carvueTapDrawBound = false
}

function bindShakeTapToEnable() {
	const root = cvQuery('.carvue-root')
	if (!root || root.__carvueShakeTapBound) return
	root.__carvueShakeTapBound = true
	root.__carvueShakeGestureHandler = function () {
		if (state._permissionGranted || state.completed) return
		const swiper = window.__carvueSwiper
		if (swiper && swiper.activeIndex !== 0) return
		if (!needsIosMotionPermission()) {
			onShakeMotionPermissionGranted()
			return
		}
		requestMotionPermissionFromGesture()
	}
	root.addEventListener('touchstart', root.__carvueShakeGestureHandler, true)
	root.addEventListener('click', root.__carvueShakeGestureHandler, true)
}

function unbindShakeTapToEnable() {
	const root = cvQuery('.carvue-root')
	if (!root || !root.__carvueShakeGestureHandler) return
	root.removeEventListener('touchstart', root.__carvueShakeGestureHandler, true)
	root.removeEventListener('click', root.__carvueShakeGestureHandler, true)
	root.__carvueShakeGestureHandler = null
	root.__carvueShakeTapBound = false
}

/** 进入摇享好礼页后初始化 */
export async function initCarShakeLottery() {
	const activity = window.__carActivityDetail
	const rawHotspot = findCarShakeHotspot(activity)
	state.hotspot = normalizeHotspot(rawHotspot)
	if (state.hotspot) {
		state.hotspot.shakeVerifyRuleCode = CAR_SHAKE_VERIFY_RULE_CODE
	}
	state.completed = false
	state.verifyStatus = null
	state.drawRecordId = null
	state.wonImage = ''
	state.verifiedImage = ''
	state._lastShakeSizeTier = null
	const stage = cvQuery('#homeShakeStage')
	const prizeImg = cvQuery('#homeShakePrizeImg')
	if (stage) stage.classList.remove('is-shake-ready')
	if (prizeImg) prizeImg.style.visibility = ''
	if (!state.hotspot) {
		console.warn('[carvue] 未找到摇一摇热点 type=21，请检查活动 pageConfigJson')
		await updatePrizeImage()
		return
	}
	bindVerifyZone()
	triggerPasswordHotspotSync()
	bindShakeTapToEnable()
	bindShakePrizeTapToDraw()
	applyVerifyZoneLayout()
	await loadDrawRecords()
	await updatePrizeImage()
	updateVerifyLayer()
	if (state.completed) return
	const DM = window.DeviceMotionEvent
	if (DM && typeof DM.requestPermission !== 'function') {
		state._permissionGranted = true
		syncShakeListener()
	} else if (state._permissionGranted) {
		syncShakeListener()
	}
}

export function destroyCarShakeLottery() {
	destroyShakeListener()
	unbindShakeTapToEnable()
	unbindShakePrizeTapToDraw()
	triggerPasswordHotspotSync()
	unbindVerifyZoneResize()
}
